import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  financeiroApi,
  FORMAS_PAGAMENTO,
  type FiltrosFinanceiro,
  type FormaPagamento,
  type ItemLancamentoEntrada,
  type LancamentoDetalhe,
  type LancamentoResumo,
  type PagamentoDetalhe,
  type ResumoFinanceiro,
  type StatusLancamento,
  type TipoDescontoItem,
  type TipoLancamento,
  type TipoLancamentoItem,
} from '@/features/financeiro/api/financeiroApi';
import { clientesApi, type ClienteResumo } from '@/features/clientes/api/clientesApi';
import {
  orcamentosApi,
  type OrcamentoDetalhe,
  type OrcamentoResumo,
} from '@/features/orcamentos/api/orcamentosApi';
import { ReciboBaixaA4 } from '@/features/financeiro/components/ReciboBaixaA4';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { imprimirComClasse, salvarComoPdf } from '@/shared/print/imprimir';
import { PrintPortal } from '@/shared/print/PrintPortal';
import { produtosApi, type ProdutoResumo } from '@/features/produtos/api/produtosApi';
import { servicosApi, type ServicoResumo } from '@/features/servicos/api/servicosApi';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SearchInput, normalizarBusca } from '@/shared/ui/SearchInput';
import { InputMonetario } from '@/shared/ui/InputMonetario';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { useSyncReload } from '@/shared/hooks/useSyncReload';
import {
  IconDollar,
  IconEdit,
  IconEye,
  IconFile,
  IconRefresh,
  IconTrash,
  IconX,
} from '@/shared/ui/Icon';
import { BotaoOcultarValores } from '@/shared/ui/BotaoOcultarValores';
import { useOcultarValores } from '@/shared/state/OcultarValoresContext';
import { Paginacao } from '@/shared/ui/Paginacao';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import { useRotaEntidade } from '@/app/useRotaEntidade';
import { ROTAS } from '@/app/rotas';

const fmtMoedaReal = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

// Bandeira de privacidade compartilhada com os subcomponentes deste arquivo.
// `FinanceiroPage` lê o contexto `useOcultarValores` e atualiza este flag
// durante o render — qualquer chamada subsequente a `fmtMoeda.format` (em
// formulários, modais, linhas da tabela) respeita o valor atual sem
// precisar threadar `ocultar` como prop por toda a árvore.
let __financeiroOcultar = false;
const fmtMoeda = {
  format(v: number): string {
    return __financeiroOcultar ? 'R$ ●●●●' : fmtMoedaReal.format(v || 0);
  },
};
const fmtData = new Intl.DateTimeFormat('pt-BR');
const fmtDataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function isoHoje(): string {
  return new Date().toISOString().slice(0, 10);
}
function isoDaqui(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}
function arred(v: number): number {
  return Math.round(v * 100) / 100;
}

function calcularDescontoItem(
  base: number,
  tipo: TipoDescontoItem | null | undefined,
  valor: number | null | undefined,
): number {
  if (!tipo || !valor || valor <= 0) return 0;
  const bruto = tipo === 'Percentual' ? (base * valor) / 100 : valor;
  return arred(Math.min(bruto, base));
}

function subtotalItem(it: ItemLancamentoEntrada): number {
  const bruto = arred(it.quantidade * it.valorUnitario);
  const desc = calcularDescontoItem(bruto, it.descontoTipo, it.descontoValor);
  return arred(bruto - desc);
}

const ROTULO_FORMA: Record<FormaPagamento, string> = Object.fromEntries(
  FORMAS_PAGAMENTO.map((f) => [f.id, f.label]),
) as Record<FormaPagamento, string>;

type Aba = 'Receber' | 'Pagar';

type ModoForm =
  | { tipo: 'fechado' }
  | { tipo: 'novo' }
  | { tipo: 'editar'; lancamento: LancamentoResumo }
  | { tipo: 'baixar'; lancamento: LancamentoResumo }
  | { tipo: 'detalhes'; lancamentoId: string };

export function FinanceiroPage() {
  const { ocultar } = useOcultarValores();
  __financeiroOcultar = ocultar;
  const [aba, setAba] = useState<Aba>('Receber');
  const [statusFiltro, setStatusFiltro] = useState<'' | StatusLancamento>('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [busca, setBusca] = useState('');
  const [clienteFiltro, setClienteFiltro] = useState('');
  // Filtro por categoria. Nasceu do PDV, que marca os consolidados de
  // fechamento de caixa com "PDV" — sem ele, uma oficina com balcão
  // movimentado não separa mais o que veio da OS do que veio do caixa.
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [categorias, setCategorias] = useState<string[]>([]);

  const [lista, setLista] = useState<LancamentoResumo[]>([]);
  const [resumo, setResumo] = useState<ResumoFinanceiro | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [clientes, setClientes] = useState<ClienteResumo[]>([]);
  const [orcamentos, setOrcamentos] = useState<OrcamentoResumo[]>([]);
  const [produtos, setProdutos] = useState<ProdutoResumo[]>([]);
  const [servicos, setServicos] = useState<ServicoResumo[]>([]);

  // Cada modo tem endereço: `/financeiro/novo`, `/financeiro/:id`,
  // `/financeiro/:id/editar` e `/financeiro/:id/baixar`.
  const rota = useRotaEntidade(ROTAS.financeiro);

  const modo = useMemo<ModoForm>(() => {
    if (rota.modo === 'novo') return { tipo: 'novo' };
    if (rota.modo === 'detalhe') return { tipo: 'detalhes', lancamentoId: rota.id };
    if (rota.modo === 'editar' || rota.modo === 'acao') {
      const lancamento = lista.find((l) => l.id === rota.id);
      if (!lancamento) return { tipo: 'fechado' };
      if (rota.modo === 'editar') return { tipo: 'editar', lancamento };
      if (rota.acao === 'baixar') return { tipo: 'baixar', lancamento };
    }
    return { tipo: 'fechado' };
  }, [rota, lista]);
  const dialogos = useDialogos();

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const filtros: FiltrosFinanceiro = {
        tipo: aba,
        status: statusFiltro || undefined,
        inicio: inicio || undefined,
        fim: fim || undefined,
        clienteId: clienteFiltro || undefined,
        categoria: categoriaFiltro || undefined,
      };
      const [l, r] = await Promise.all([
        financeiroApi.listar(filtros),
        financeiroApi.resumo({ tipo: aba, inicio: inicio || undefined, fim: fim || undefined }),
      ]);
      setLista(l);
      setResumo(r);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar.');
    } finally {
      setCarregando(false);
    }
  }, [aba, statusFiltro, inicio, fim, clienteFiltro, categoriaFiltro]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Um lançamento criado, baixado ou cancelado na outra ponta (o app web,
  // outro desktop) tem de aparecer aqui sem F5 — é o mesmo tratamento que
  // clientes, produtos, estoque, ponto e O.S. já tinham.
  useSyncReload(carregar);

  // Categorias em uso, para o select — recarrega junto com a lista para
  // que o "PDV" apareça assim que o primeiro caixa for fechado.
  useEffect(() => {
    void financeiroApi
      .listarCategorias()
      .then(setCategorias)
      .catch(() => setCategorias([]));
  }, [lista]);

  useEffect(() => {
    void Promise.all([
      clientesApi.listar(),
      orcamentosApi.listar(),
      produtosApi.listar(),
      servicosApi.listar(),
    ])
      .then(([c, o, p, s]) => {
        setClientes(c);
        setOrcamentos(o);
        setProdutos(p);
        setServicos(s);
      })
      .catch(() => undefined);
  }, []);

  const filtrados = useMemo(() => {
    const q = normalizarBusca(busca.trim());
    if (q === '') return lista;
    return lista.filter((l) => {
      const alvo = normalizarBusca(
        `${l.descricao} ${l.categoria ?? ''} ${l.fornecedor ?? ''} ${l.clienteNome ?? ''} ${l.orcamentoNumero ?? ''}`,
      );
      return alvo.includes(q);
    });
  }, [lista, busca]);

  const { pagina, setPagina, paginados, total, totalPaginas, tamanhoPagina } =
    usePaginacao(filtrados, 25);

  async function aoExcluir(l: LancamentoResumo) {
    const ok = await dialogos.confirmar({
      titulo: 'Excluir lançamento',
      mensagem: `Excluir o lançamento "${l.descricao}"?`,
      textoConfirmar: 'Excluir',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await financeiroApi.excluir(l.id);
      await carregar();
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao excluir.');
    }
  }

  async function aoCancelar(l: LancamentoResumo) {
    const ok = await dialogos.confirmar({
      titulo: 'Cancelar lançamento',
      mensagem: `Cancelar o lançamento "${l.descricao}"?`,
      textoConfirmar: 'Cancelar lançamento',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await financeiroApi.cancelar(l.id);
      await carregar();
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao cancelar.');
    }
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Financeiro"
        descricao="Contas a receber e a pagar com baixas parciais, histórico de pagamentos e vínculo a ordens de serviço."
        acoes={
          <>
            <BotaoOcultarValores />
            <Button onClick={() => void carregar()} disabled={carregando}>
              <IconRefresh className="btn__icon" />
              {carregando ? 'Atualizando…' : 'Atualizar'}
            </Button>
            <Button variante="primary" onClick={() => rota.abrir('novo')}>
              + Novo lançamento
            </Button>
          </>
        }
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          {erro}
        </p>
      )}

      <div className="tabs" role="tablist" aria-label="Tipo de lançamento">
        {(['Receber', 'Pagar'] as Aba[]).map((a) => (
          <button
            key={a}
            role="tab"
            aria-selected={aba === a}
            className={`tabs__item${aba === a ? ' tabs__item--ativa' : ''}`}
            onClick={() => {
              setAba(a);
              setStatusFiltro('');
              setBusca('');
            }}
          >
            {a === 'Receber' ? 'Contas a Receber' : 'Contas a Pagar'}
          </button>
        ))}
      </div>

      {resumo && (
        <div className="kpi-grid">
          <KpiCard
            titulo={aba === 'Receber' ? 'A receber' : 'A pagar'}
            valor={fmtMoeda.format(resumo.totalAberto)}
            descricao={`${resumo.qtdAberto} em aberto`}
            tom="neutro"
          />
          <KpiCard
            titulo="Vencido"
            valor={fmtMoeda.format(resumo.totalVencido)}
            descricao={`${resumo.qtdVencido} em atraso`}
            tom={resumo.totalVencido > 0 ? 'alerta' : 'neutro'}
          />
          <KpiCard
            titulo="A vencer"
            valor={fmtMoeda.format(resumo.totalAVencer)}
            descricao="Em aberto, dentro do prazo"
            tom="info"
          />
          <KpiCard
            titulo={aba === 'Receber' ? 'Recebido' : 'Pago'}
            valor={fmtMoeda.format(resumo.totalPago)}
            descricao={`${resumo.qtdPago} quitado(s)`}
            tom="sucesso"
          />
        </div>
      )}

      <div className="card">
        <div className="card__body">
          <div className="filtros-grid">
            <Field rotulo="Status">
              {(id) => (
                <select
                  id={id}
                  className="input"
                  value={statusFiltro}
                  onChange={(e) => setStatusFiltro((e.target.value as StatusLancamento) || '')}
                >
                  <option value="">Todos</option>
                  <option value="EmAberto">Em aberto</option>
                  <option value="Pago">Pago</option>
                  <option value="Cancelado">Cancelado</option>
                </select>
              )}
            </Field>
            <Field rotulo="Vencimento de">
              {(id) => (
                <input
                  id={id}
                  type="date"
                  className="input"
                  value={inicio}
                  onChange={(e) => setInicio(e.target.value)}
                />
              )}
            </Field>
            <Field rotulo="Vencimento até">
              {(id) => (
                <input
                  id={id}
                  type="date"
                  className="input"
                  value={fim}
                  onChange={(e) => setFim(e.target.value)}
                />
              )}
            </Field>
            <Field rotulo="Cliente">
              {(id) => (
                <select
                  id={id}
                  className="input"
                  value={clienteFiltro}
                  onChange={(e) => setClienteFiltro(e.target.value)}
                >
                  <option value="">Todos</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field rotulo="Categoria">
              {(id) => (
                <select
                  id={id}
                  className="input"
                  value={categoriaFiltro}
                  onChange={(e) => setCategoriaFiltro(e.target.value)}
                >
                  <option value="">Todas</option>
                  {categorias.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__header card__header--com-busca">
          <span className="card__title">
            {filtrados.length} de {lista.length} lançamento(s)
          </span>
          <SearchInput
            valor={busca}
            onChange={setBusca}
            placeholder="Buscar por descrição, categoria, cliente, ordem de serviço…"
            ariaLabel="Buscar lançamento"
          />
        </div>
        <div className="card__body card__body--flush">
          {lista.length === 0 && !carregando ? (
            <EmptyState
              icone={<IconFile />}
              titulo={
                aba === 'Receber'
                  ? 'Nenhuma conta a receber no período.'
                  : 'Nenhuma conta a pagar no período.'
              }
              descricao="Cadastre um lançamento ou ajuste os filtros."
            />
          ) : filtrados.length === 0 ? (
            <EmptyState
              icone={<IconFile />}
              titulo="Nenhum lançamento corresponde à busca."
              descricao="Tente outro termo ou limpe o campo de busca."
            />
          ) : (
            <div className="table-scroll">
              <table className="table table--responsive">
                <thead>
                  <tr>
                    <th>Descrição</th>
                    <th>{aba === 'Receber' ? 'Cliente' : 'Fornecedor'}</th>
                    <th>Vencimento</th>
                    <th className="table__numeric">Valor</th>
                    <th className="table__numeric">Saldo</th>
                    <th>Status</th>
                    <th style={{ width: 1 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginados.map((l) => (
                    <LinhaLancamento
                      key={l.id}
                      l={l}
                      aba={aba}
                      onAbrir={() => rota.abrir(l.id)}
                      onEditar={() => rota.abrir(`${l.id}/editar`)}
                      onBaixar={() => rota.abrir(`${l.id}/baixar`)}
                      onCancelar={() => void aoCancelar(l)}
                      onExcluir={() => void aoExcluir(l)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Paginacao
            pagina={pagina}
            totalPaginas={totalPaginas}
            total={total}
            tamanhoPagina={tamanhoPagina}
            onMudarPagina={setPagina}
          />
        </div>
      </div>

      {modo.tipo === 'novo' && (
        <FormLancamentoModal
          aba={aba}
          clientes={clientes}
          orcamentos={orcamentos}
          produtos={produtos}
          servicos={servicos}
          aoFechar={rota.fechar}
          aoSalvar={async () => {
            rota.fechar();
            await carregar();
          }}
        />
      )}
      {modo.tipo === 'editar' && (
        <FormLancamentoModal
          aba={modo.lancamento.tipo}
          clientes={clientes}
          orcamentos={orcamentos}
          produtos={produtos}
          servicos={servicos}
          edicaoId={modo.lancamento.id}
          aoFechar={rota.fechar}
          aoSalvar={async () => {
            rota.fechar();
            await carregar();
          }}
        />
      )}
      {modo.tipo === 'baixar' && (
        <BaixarLancamentoModal
          lancamento={modo.lancamento}
          aoFechar={rota.fechar}
          aoSalvar={async (manterAberto) => {
            await carregar();
            if (manterAberto) {
              rota.abrir(modo.lancamento.id);
            } else {
              rota.fechar();
            }
          }}
        />
      )}
      {modo.tipo === 'detalhes' && (
        <DetalhesLancamentoModal
          lancamentoId={modo.lancamentoId}
          aoFechar={rota.fechar}
          aoBaixar={(l) => rota.abrir(`${l.id}/baixar`)}
          aoMudar={() => void carregar()}
        />
      )}
    </section>
  );
}

function LinhaLancamento({
  l,
  aba,
  onAbrir,
  onEditar,
  onBaixar,
  onCancelar,
  onExcluir,
}: {
  l: LancamentoResumo;
  aba: Aba;
  onAbrir: () => void;
  onEditar: () => void;
  onBaixar: () => void;
  onCancelar: () => void;
  onExcluir: () => void;
}) {
  const venc = new Date(l.vencimento);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const vencido = l.status === 'EmAberto' && venc < hoje;
  return (
    <tr>
      <td className="table__primary">
        <button
          type="button"
          onClick={onAbrir}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'inherit',
            font: 'inherit',
            textAlign: 'left',
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
          }}
        >
          {l.descricao}
        </button>
        <div style={{ marginTop: 4 }}>
          <BadgeSyncPendente pendente={l.syncPendente} />
        </div>
        <div className="table__muted" style={{ fontSize: 12, marginTop: 2 }}>
          {l.orcamentoNumero ? `Ordem de serviço ${l.orcamentoNumero} · ` : ''}
          {l.quantidadeItens > 0 ? `${l.quantidadeItens} item(ns) · ` : ''}
          {l.quantidadePagamentos > 0 ? `${l.quantidadePagamentos} pagamento(s)` : 'sem pagamentos'}
        </div>
      </td>
      <td className="table__muted" data-label={aba === 'Receber' ? 'Cliente' : 'Fornecedor'}>
        {aba === 'Receber' ? l.clienteNome ?? '—' : l.fornecedor ?? l.clienteNome ?? '—'}
      </td>
      <td className={vencido ? 'table__primary' : 'table__muted'} data-label="Vencimento">
        {fmtData.format(venc)}
        {vencido && (
          <div style={{ color: 'var(--color-danger)', fontSize: 12 }}>
            {l.diasAtraso} {l.diasAtraso === 1 ? 'dia' : 'dias'} em atraso
          </div>
        )}
      </td>
      <td className="table__numeric" data-label="Valor">{fmtMoeda.format(l.valor)}</td>
      <td className="table__numeric" data-label="Saldo">
        <strong>{fmtMoeda.format(l.saldo)}</strong>
        {l.valorPago > 0 && l.status !== 'Pago' && (
          <div className="table__muted" style={{ fontSize: 12 }}>
            Pago: {fmtMoeda.format(l.valorPago)}
          </div>
        )}
      </td>
      <td data-label="Status">
        <BadgeStatus status={l.status} vencido={vencido} parcial={l.valorPago > 0 && l.status === 'EmAberto'} />
      </td>
      <td>
        <div className="row-actions">
          {l.status === 'EmAberto' && (
            <Button
              variante="primary"
              className="btn--icon"
              onClick={onBaixar}
              aria-label={`Baixar ${l.descricao}`}
            >
              <IconDollar />
            </Button>
          )}
          <Button
            className="btn--icon"
            onClick={onAbrir}
            aria-label={`Abrir ${l.descricao}`}
          >
            <IconEye />
          </Button>
          {l.status === 'EmAberto' && l.valorPago === 0 && (
            <Button
              className="btn--icon"
              onClick={onEditar}
              aria-label={`Editar ${l.descricao}`}
            >
              <IconEdit />
            </Button>
          )}
          {l.status === 'EmAberto' && (
            <Button
              className="btn--icon"
              onClick={onCancelar}
              aria-label={`Cancelar ${l.descricao}`}
            >
              <IconX />
            </Button>
          )}
          {l.valorPago === 0 && l.status !== 'Pago' && (
            <Button
              className="btn--icon btn--danger"
              onClick={onExcluir}
              aria-label={`Excluir ${l.descricao}`}
            >
              <IconTrash />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function BadgeStatus({
  status,
  vencido,
  parcial,
}: {
  status: StatusLancamento;
  vencido: boolean;
  parcial: boolean;
}) {
  if (status === 'Pago') return <span className="badge badge--ativo">Pago</span>;
  if (status === 'Cancelado') return <span className="badge badge--cancelado">Cancelado</span>;
  if (vencido) return <span className="badge badge--recusado">Vencido{parcial ? ' · parcial' : ''}</span>;
  if (parcial) return <span className="badge badge--inativo">Parcial</span>;
  return <span className="badge badge--enviado">Em aberto</span>;
}

function KpiCard({
  titulo,
  valor,
  descricao,
  tom,
}: {
  titulo: string;
  valor: string;
  descricao: string;
  tom: 'neutro' | 'info' | 'sucesso' | 'alerta';
}) {
  return (
    <div className={`kpi kpi--${tom}`}>
      <span className="kpi__rotulo">{titulo}</span>
      <strong className="kpi__valor">{valor}</strong>
      <span className="kpi__descricao">{descricao}</span>
    </div>
  );
}

/* ============================================================
   Form de criação/edição com orçamento + itens
   ============================================================ */

function CampoLeitura({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="campo-leitura">
      <span className="field__label">{rotulo}</span>
      <div className="input input--readonly">{children}</div>
    </div>
  );
}

type ItemForm = ItemLancamentoEntrada & { uid: string };

function novoItem(parcial: Partial<ItemLancamentoEntrada> = {}): ItemForm {
  return {
    uid: crypto.randomUUID(),
    tipo: 'Outro',
    descricao: '',
    quantidade: 1,
    valorUnitario: 0,
    referenciaId: null,
    orcamentoItemId: null,
    descontoTipo: null,
    descontoValor: null,
    ...parcial,
  };
}

type FormLancamentoProps = {
  aba: TipoLancamento;
  clientes: ClienteResumo[];
  orcamentos: OrcamentoResumo[];
  produtos: ProdutoResumo[];
  servicos: ServicoResumo[];
  edicaoId?: string;
  aoFechar: () => void;
  aoSalvar: () => Promise<void>;
};

function FormLancamentoModal({
  aba,
  clientes,
  orcamentos,
  produtos,
  servicos,
  edicaoId,
  aoFechar,
  aoSalvar,
}: FormLancamentoProps) {
  const editando = Boolean(edicaoId);
  const [carregandoEdicao, setCarregandoEdicao] = useState(editando);
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('');
  const [valor, setValor] = useState(0);
  const [emissao, setEmissao] = useState(isoHoje());
  const [vencimento, setVencimento] = useState(isoDaqui(30));
  const [clienteId, setClienteId] = useState<string>('');
  const [orcamentoId, setOrcamentoId] = useState<string>('');
  const [fornecedor, setFornecedor] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<ItemForm[]>([]);
  const [usarSomaItens, setUsarSomaItens] = useState(true);
  const [carregandoItensOrcamento, setCarregandoItensOrcamento] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!edicaoId) return;
    let cancelado = false;
    setCarregandoEdicao(true);
    financeiroApi
      .obter(edicaoId)
      .then((d) => {
        if (cancelado) return;
        setDescricao(d.descricao);
        setCategoria(d.categoria ?? '');
        setValor(d.valor);
        setEmissao(d.emissao.slice(0, 10));
        setVencimento(d.vencimento.slice(0, 10));
        setClienteId(d.clienteId ?? '');
        setOrcamentoId(d.orcamentoId ?? '');
        setFornecedor(d.fornecedor ?? '');
        setObservacoes(d.observacoes ?? '');
        const its = d.itens.map((it) =>
          novoItem({
            tipo: it.tipo,
            descricao: it.descricao,
            quantidade: it.quantidade,
            valorUnitario: it.valorUnitario,
            referenciaId: it.referenciaId,
            orcamentoItemId: it.orcamentoItemId,
            descontoTipo: it.descontoTipo,
            descontoValor: it.descontoValor,
          }),
        );
        setItens(its);
        const soma = its.reduce((acc, it) => acc + subtotalItem(it), 0);
        setUsarSomaItens(its.length > 0 && Math.abs(arred(soma) - d.valor) < 0.01);
      })
      .catch((e: unknown) => {
        if (!cancelado) setErro(e instanceof Error ? e.message : 'Falha ao carregar lançamento.');
      })
      .finally(() => {
        if (!cancelado) setCarregandoEdicao(false);
      });
    return () => {
      cancelado = true;
    };
  }, [edicaoId]);

  const orcamentosDoTipo = useMemo(() => {
    if (aba !== 'Receber') return orcamentos;
    return orcamentos;
  }, [orcamentos, aba]);

  const orcamentoSelecionado = useMemo(
    () => orcamentos.find((o) => o.id === orcamentoId) ?? null,
    [orcamentos, orcamentoId],
  );

  async function aoMudarOrcamento(id: string) {
    setOrcamentoId(id);
    if (!id) return;
    const orc = orcamentos.find((o) => o.id === id);
    if (!orc) return;
    if (!clienteId) setClienteId(orc.clienteId);
    setCarregandoItensOrcamento(true);
    try {
      const itensOrc = await financeiroApi.itensDoOrcamento(id);
      const formItens = itensOrc.map((it) => novoItem(it));
      setItens(formItens);
      setUsarSomaItens(true);
      if (!descricao.trim()) setDescricao(`Recebimento ordem de serviço ${orc.numero}`);
      const soma = formItens.reduce((acc, it) => acc + subtotalItem(it), 0);
      setValor(arred(soma));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar itens da ordem de serviço.');
    } finally {
      setCarregandoItensOrcamento(false);
    }
  }

  function atualizarItem(uid: string, patch: Partial<ItemForm>) {
    setItens((arr) => arr.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));
  }

  function removerItem(uid: string) {
    setItens((arr) => arr.filter((it) => it.uid !== uid));
  }

  function adicionarItem(tipo: TipoLancamentoItem) {
    setItens((arr) => [...arr, novoItem({ tipo })]);
  }

  function aoEscolherProduto(uid: string, produtoId: string) {
    const p = produtos.find((x) => x.id === produtoId);
    if (!p) {
      atualizarItem(uid, { referenciaId: null });
      return;
    }
    atualizarItem(uid, {
      referenciaId: p.id,
      descricao: `${p.codigo} — ${p.nome}`,
      valorUnitario: p.precoUnitario,
    });
  }

  function aoEscolherServico(uid: string, servicoId: string) {
    const s = servicos.find((x) => x.id === servicoId);
    if (!s) {
      atualizarItem(uid, { referenciaId: null });
      return;
    }
    atualizarItem(uid, {
      referenciaId: s.id,
      descricao: `${s.codigo} — ${s.nome}`,
      valorUnitario: s.precoUnitario,
    });
  }

  const totalItens = useMemo(
    () => arred(itens.reduce((acc, it) => acc + subtotalItem(it), 0)),
    [itens],
  );

  const totalBrutoItens = useMemo(
    () => arred(itens.reduce((acc, it) => acc + it.quantidade * it.valorUnitario, 0)),
    [itens],
  );

  const totalDescontoItens = useMemo(
    () => arred(totalBrutoItens - totalItens),
    [totalBrutoItens, totalItens],
  );

  useEffect(() => {
    if (usarSomaItens && itens.length > 0) {
      setValor(totalItens);
    }
  }, [usarSomaItens, totalItens, itens.length]);

  async function aoSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (descricao.trim().length < 2) {
      setErro('Descrição deve ter ao menos 2 caracteres.');
      return;
    }
    if (aba === 'Receber' && !clienteId) {
      setErro('Selecione o cliente para conta a receber.');
      return;
    }
    const valorFinal = usarSomaItens && itens.length > 0 ? totalItens : valor;
    if (valorFinal <= 0) {
      setErro('Valor deve ser maior que zero.');
      return;
    }
    for (const it of itens) {
      if (!it.descricao.trim()) {
        setErro('Cada item precisa de descrição.');
        return;
      }
      if (it.quantidade <= 0) {
        setErro(`Quantidade do item "${it.descricao}" deve ser > 0.`);
        return;
      }
      if (it.valorUnitario < 0) {
        setErro(`Valor unitário do item "${it.descricao}" inválido.`);
        return;
      }
    }
    const itensPayload = itens.map(({ uid: _u, ...rest }) => rest);
    setSalvando(true);
    try {
      if (editando && edicaoId) {
        await financeiroApi.atualizar(edicaoId, {
          descricao: descricao.trim(),
          categoria: categoria.trim() || null,
          valor: valorFinal,
          vencimento: new Date(vencimento).toISOString(),
          clienteId: clienteId || null,
          fornecedor: aba === 'Pagar' ? fornecedor.trim() || null : null,
          observacoes: observacoes.trim() || null,
          itens: itensPayload,
        });
      } else {
        await financeiroApi.criar({
          tipo: aba,
          descricao: descricao.trim(),
          categoria: categoria.trim() || null,
          valor: valorFinal,
          emissao: new Date(emissao).toISOString(),
          vencimento: new Date(vencimento).toISOString(),
          clienteId: clienteId || null,
          fornecedor: aba === 'Pagar' ? fornecedor.trim() || null : null,
          orcamentoId: orcamentoId || null,
          observacoes: observacoes.trim() || null,
          itens: itensPayload,
        });
      }
      await aoSalvar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  const titulo = editando
    ? `Editar conta a ${aba === 'Receber' ? 'receber' : 'pagar'}`
    : `Nova conta a ${aba === 'Receber' ? 'receber' : 'pagar'}`;

  return (
    <Modal
      aberto
      tamanho="lg"
      titulo={titulo}
      onFechar={aoFechar}
      rodape={
        <>
          <Button onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variante="primary"
            onClick={(e) => void aoSubmit(e as unknown as React.FormEvent)}
            disabled={salvando || carregandoEdicao}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      {carregandoEdicao ? (
        <p className="hint">Carregando lançamento…</p>
      ) : (
        <form className="form-grid" onSubmit={aoSubmit}>
          {erro && <div className="form-error">{erro}</div>}

          {aba === 'Receber' && !editando && (
            <Field
              rotulo="Vincular a uma ordem de serviço"
              dica="Opcional. Ao escolher, herda o cliente e pré-carrega peças e serviços da ordem de serviço."
            >
              {(id) => (
                <select
                  id={id}
                  className="input"
                  value={orcamentoId}
                  onChange={(e) => void aoMudarOrcamento(e.target.value)}
                  disabled={carregandoItensOrcamento}
                >
                  <option value="">— sem vínculo —</option>
                  {orcamentosDoTipo.map((o) => {
                    const c = clientes.find((cl) => cl.id === o.clienteId);
                    return (
                      <option key={o.id} value={o.id}>
                        {o.numero} · {c?.nome ?? '—'} · {fmtMoeda.format(o.total)}
                      </option>
                    );
                  })}
                </select>
              )}
            </Field>
          )}

          {orcamentoSelecionado && (
            <div className="info-banner">
              <div>
                <strong>Ordem de serviço {orcamentoSelecionado.numero}</strong>
                <div className="hint" style={{ marginTop: 2 }}>
                  Cliente herdado · Total {fmtMoeda.format(orcamentoSelecionado.total)} ·{' '}
                  {orcamentoSelecionado.veiculoDescricao}
                </div>
              </div>
            </div>
          )}

          <Field rotulo="Descrição" obrigatorio>
            {(id) => (
              <input
                id={id}
                className="input"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                required
                maxLength={300}
              />
            )}
          </Field>

          <div className="grid-2">
            <Field rotulo="Categoria" dica="Opcional. Ex.: Mão de obra, Aluguel, Energia.">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  maxLength={100}
                />
              )}
            </Field>
            <Field
              rotulo={
                usarSomaItens && itens.length > 0
                  ? 'Valor (somado dos itens)'
                  : 'Valor'
              }
              obrigatorio={!(usarSomaItens && itens.length > 0)}
              dica={
                itens.length > 0
                  ? usarSomaItens
                    ? 'Calculado automaticamente. Desmarque para informar manualmente.'
                    : 'Valor manual diferente da soma dos itens.'
                  : undefined
              }
            >
              {(id) => (
                <InputMonetario
                  id={id}
                  valor={valor}
                  onChange={setValor}
                  disabled={usarSomaItens && itens.length > 0}
                />
              )}
            </Field>
          </div>

          {itens.length > 0 && (
            <label className="checkbox checkbox--compact">
              <input
                type="checkbox"
                checked={usarSomaItens}
                onChange={(e) => setUsarSomaItens(e.target.checked)}
              />
              <span>Usar soma dos itens como valor do lançamento</span>
            </label>
          )}

          <div className="grid-2">
            {!editando && (
              <Field rotulo="Emissão" obrigatorio>
                {(id) => (
                  <input
                    id={id}
                    type="date"
                    className="input"
                    value={emissao}
                    onChange={(e) => setEmissao(e.target.value)}
                    required
                  />
                )}
              </Field>
            )}
            <Field rotulo="Vencimento" obrigatorio>
              {(id) => (
                <input
                  id={id}
                  type="date"
                  className="input"
                  value={vencimento}
                  onChange={(e) => setVencimento(e.target.value)}
                  required
                />
              )}
            </Field>
          </div>

          {aba === 'Receber' ? (
            <Field rotulo="Cliente" obrigatorio>
              {(id) => (
                <select
                  id={id}
                  className="input"
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                  required
                  disabled={Boolean(orcamentoSelecionado)}
                >
                  <option value="">Selecione…</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : (
            <div className="grid-2">
              <Field rotulo="Fornecedor" dica="Opcional. Nome do prestador ou fornecedor.">
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    value={fornecedor}
                    onChange={(e) => setFornecedor(e.target.value)}
                    maxLength={200}
                  />
                )}
              </Field>
              <Field rotulo="Cliente associado" dica="Opcional. Caso o custo seja de um cliente.">
                {(id) => (
                  <select
                    id={id}
                    className="input"
                    value={clienteId}
                    onChange={(e) => setClienteId(e.target.value)}
                  >
                    <option value="">—</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>
          )}

          <fieldset className="fieldset">
            <legend className="fieldset__legend">
              Itens (peças/serviços) — opcional
            </legend>
            {carregandoItensOrcamento && <p className="hint">Carregando itens da ordem de serviço…</p>}
            {itens.length === 0 ? (
              <p className="hint">
                Nenhum item. Você pode lançar apenas com valor total ou adicionar peças/serviços.
              </p>
            ) : (
              <>
                <ol className="itens-cards">
                  {itens.map((it, idx) => (
                    <li key={it.uid} className="itens-cards__item">
                      <div className="itens-cards__cabecalho">
                        <span className="itens-cards__indice">Item {idx + 1}</span>
                        <Button
                          type="button"
                          className="btn--danger"
                          onClick={() => removerItem(it.uid)}
                          aria-label="Remover item"
                        >
                          Remover
                        </Button>
                      </div>

                      <div className="itens-cards__grid">
                        <Field rotulo="Tipo">
                          {(id) => (
                            <select
                              id={id}
                              className="input"
                              value={it.tipo}
                              onChange={(e) =>
                                atualizarItem(it.uid, {
                                  tipo: e.target.value as TipoLancamentoItem,
                                  referenciaId: null,
                                })
                              }
                            >
                              <option value="Produto">Peça</option>
                              <option value="Servico">Serviço</option>
                              <option value="Outro">Outro</option>
                            </select>
                          )}
                        </Field>

                        <Field rotulo={it.tipo === 'Outro' ? 'Descrição' : 'Item do catálogo'} obrigatorio>
                          {(id) =>
                            it.tipo === 'Produto' ? (
                              <select
                                id={id}
                                className="input"
                                value={it.referenciaId ?? ''}
                                onChange={(e) => aoEscolherProduto(it.uid, e.target.value)}
                              >
                                <option value="">— escolha a peça —</option>
                                {produtos.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.codigo} — {p.nome}
                                  </option>
                                ))}
                              </select>
                            ) : it.tipo === 'Servico' ? (
                              <select
                                id={id}
                                className="input"
                                value={it.referenciaId ?? ''}
                                onChange={(e) => aoEscolherServico(it.uid, e.target.value)}
                              >
                                <option value="">— escolha o serviço —</option>
                                {servicos.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.codigo} — {s.nome}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                id={id}
                                className="input"
                                value={it.descricao}
                                onChange={(e) =>
                                  atualizarItem(it.uid, { descricao: e.target.value })
                                }
                                placeholder="Descrição livre"
                                maxLength={500}
                              />
                            )
                          }
                        </Field>
                      </div>

                      {(it.tipo === 'Produto' || it.tipo === 'Servico') && it.descricao && (
                        <p className="hint itens-cards__descricao">{it.descricao}</p>
                      )}

                      <div className="itens-cards__valores">
                        <Field rotulo="Quantidade" obrigatorio>
                          {(id) => (
                            <input
                              id={id}
                              className="input"
                              type="number"
                              min="0"
                              step="1"
                              value={it.quantidade}
                              onChange={(e) =>
                                atualizarItem(it.uid, {
                                  quantidade: Math.max(0, Number(e.target.value) || 0),
                                })
                              }
                            />
                          )}
                        </Field>
                        <Field rotulo="Valor unitário">
                          {(id) => (
                            <InputMonetario
                              id={id}
                              valor={it.valorUnitario}
                              onChange={(v) => atualizarItem(it.uid, { valorUnitario: v })}
                            />
                          )}
                        </Field>
                        <Field rotulo="Desconto">
                          {() => (
                            <div className="itens-cards__desconto">
                              <select
                                aria-label="Tipo do desconto"
                                className="input"
                                value={it.descontoTipo ?? 'Percentual'}
                                onChange={(e) =>
                                  atualizarItem(it.uid, {
                                    descontoTipo: e.target.value as TipoDescontoItem,
                                  })
                                }
                              >
                                <option value="Percentual">%</option>
                                <option value="Valor">R$</option>
                              </select>
                              <InputMonetario
                                ariaLabel="Valor do desconto"
                                valor={it.descontoValor ?? 0}
                                onChange={(v) =>
                                  atualizarItem(it.uid, {
                                    descontoTipo: it.descontoTipo ?? 'Percentual',
                                    descontoValor: v,
                                  })
                                }
                              />
                            </div>
                          )}
                        </Field>
                      </div>

                      <div className="itens-cards__resumo">
                        {(() => {
                          const bruto = arred(it.quantidade * it.valorUnitario);
                          const desc = calcularDescontoItem(
                            bruto,
                            it.descontoTipo,
                            it.descontoValor,
                          );
                          const liq = arred(bruto - desc);
                          return (
                            <>
                              <div className="itens-cards__resumo-linha">
                                <span>Subtotal bruto</span>
                                <span>{fmtMoeda.format(bruto)}</span>
                              </div>
                              {desc > 0 && (
                                <div className="itens-cards__resumo-linha itens-cards__resumo-linha--desc">
                                  <span>Desconto</span>
                                  <span>− {fmtMoeda.format(desc)}</span>
                                </div>
                              )}
                              <div className="itens-cards__resumo-linha itens-cards__resumo-linha--total">
                                <span>Subtotal</span>
                                <strong>{fmtMoeda.format(liq)}</strong>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="itens-cards__total">
                  {totalDescontoItens > 0 ? (
                    <>
                      <div className="itens-cards__total-linha">
                        <span>Total bruto</span>
                        <span>{fmtMoeda.format(totalBrutoItens)}</span>
                      </div>
                      <div className="itens-cards__total-linha itens-cards__total-linha--desc">
                        <span>Total de desconto</span>
                        <span>− {fmtMoeda.format(totalDescontoItens)}</span>
                      </div>
                      <div className="itens-cards__total-linha itens-cards__total-linha--final">
                        <span>Total dos itens</span>
                        <strong>{fmtMoeda.format(totalItens)}</strong>
                      </div>
                    </>
                  ) : (
                    <div className="itens-cards__total-linha itens-cards__total-linha--final">
                      <span>Total dos itens</span>
                      <strong>{fmtMoeda.format(totalItens)}</strong>
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="row-actions" style={{ marginTop: 12 }}>
              <Button type="button" onClick={() => adicionarItem('Produto')}>
                + Peça
              </Button>
              <Button type="button" onClick={() => adicionarItem('Servico')}>
                + Serviço
              </Button>
              <Button type="button" onClick={() => adicionarItem('Outro')}>
                + Outro
              </Button>
            </div>
          </fieldset>

          <Field rotulo="Observações" dica="Opcional.">
            {(id) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                maxLength={2000}
              />
            )}
          </Field>
        </form>
      )}
    </Modal>
  );
}

/* ============================================================
   Modal de baixa parcial / total
   ============================================================ */

type BaixarProps = {
  lancamento: LancamentoResumo;
  aoFechar: () => void;
  aoSalvar: (manterAberto: boolean) => Promise<void>;
};

function BaixarLancamentoModal({ lancamento, aoFechar, aoSalvar }: BaixarProps) {
  const [valor, setValor] = useState(lancamento.saldo);
  const [data, setData] = useState(isoHoje());
  const [forma, setForma] = useState<FormaPagamento>('Pix');
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const baixaParcial = valor < lancamento.saldo;

  async function aoSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (valor <= 0) {
      setErro('Valor da baixa deve ser maior que zero.');
      return;
    }
    if (valor > lancamento.saldo + 0.001) {
      setErro(`Valor excede o saldo em aberto (${fmtMoeda.format(lancamento.saldo)}).`);
      return;
    }
    setSalvando(true);
    try {
      await financeiroApi.baixar(lancamento.id, {
        valor,
        data: new Date(data).toISOString(),
        formaPagamento: forma,
        observacao: observacao.trim() || null,
      });
      await aoSalvar(baixaParcial);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao registrar baixa.');
    } finally {
      setSalvando(false);
    }
  }

  function preencherSaldoTotal() {
    setValor(lancamento.saldo);
  }
  function preencherMetade() {
    setValor(arred(lancamento.saldo / 2));
  }

  return (
    <Modal
      aberto
      titulo={`Baixar ${lancamento.tipo === 'Receber' ? 'recebimento' : 'pagamento'}`}
      onFechar={aoFechar}
      rodape={
        <>
          <Button onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variante="primary"
            onClick={(e) => void aoSubmit(e as unknown as React.FormEvent)}
            disabled={salvando}
          >
            {salvando
              ? 'Salvando…'
              : baixaParcial
                ? 'Confirmar baixa parcial'
                : 'Confirmar baixa total'}
          </Button>
        </>
      }
    >
      <form className="form-grid" onSubmit={aoSubmit}>
        {erro && <div className="form-error">{erro}</div>}

        <div className="resumo-baixa">
          <div>
            <span className="resumo-baixa__rotulo">Lançamento</span>
            <strong>{lancamento.descricao}</strong>
          </div>
          <div>
            <span className="resumo-baixa__rotulo">Valor original</span>
            <strong>{fmtMoeda.format(lancamento.valor)}</strong>
          </div>
          <div>
            <span className="resumo-baixa__rotulo">
              Já {lancamento.tipo === 'Receber' ? 'recebido' : 'pago'}
            </span>
            <strong>{fmtMoeda.format(lancamento.valorPago)}</strong>
          </div>
          <div>
            <span className="resumo-baixa__rotulo">Saldo em aberto</span>
            <strong style={{ color: 'var(--color-primary)' }}>
              {fmtMoeda.format(lancamento.saldo)}
            </strong>
          </div>
        </div>

        <div className="grid-2">
          <Field rotulo="Valor da baixa" obrigatorio>
            {(id) => <InputMonetario id={id} valor={valor} onChange={setValor} />}
          </Field>
          <Field rotulo="Data do pagamento" obrigatorio>
            {(id) => (
              <input
                id={id}
                type="date"
                className="input"
                value={data}
                onChange={(e) => setData(e.target.value)}
                required
              />
            )}
          </Field>
        </div>

        <div className="row-actions">
          <Button type="button" onClick={preencherSaldoTotal}>
            Saldo total ({fmtMoeda.format(lancamento.saldo)})
          </Button>
          <Button type="button" onClick={preencherMetade}>
            Metade ({fmtMoeda.format(arred(lancamento.saldo / 2))})
          </Button>
        </div>

        <Field rotulo="Forma de pagamento">
          {(id) => (
            <select
              id={id}
              className="input"
              value={forma}
              onChange={(e) => setForma(e.target.value as FormaPagamento)}
            >
              {FORMAS_PAGAMENTO.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field rotulo="Observação da baixa" dica="Opcional. Ex.: número do comprovante, conta.">
          {(id) => (
            <textarea
              id={id}
              className="input"
              rows={2}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              maxLength={500}
            />
          )}
        </Field>

        <div className={`info-banner ${baixaParcial ? 'info-banner--alerta' : 'info-banner--sucesso'}`}>
          {baixaParcial ? (
            <>
              <strong>Baixa parcial.</strong> Restará{' '}
              <strong>{fmtMoeda.format(arred(lancamento.saldo - valor))}</strong> em aberto.
              O lançamento permanece pendente.
            </>
          ) : (
            <>
              <strong>Baixa total.</strong> Lançamento será marcado como{' '}
              <strong>Pago</strong>.
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================
   Modal de detalhes com timeline de pagamentos
   ============================================================ */

type DetalhesProps = {
  lancamentoId: string;
  aoFechar: () => void;
  aoBaixar: (l: LancamentoResumo) => void;
  aoMudar: () => void;
};

function DetalhesLancamentoModal({ lancamentoId, aoFechar, aoBaixar, aoMudar }: DetalhesProps) {
  const [detalhe, setDetalhe] = useState<LancamentoDetalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [orcamentoDetalhe, setOrcamentoDetalhe] = useState<OrcamentoDetalhe | null>(null);
  const [pagamentoAImprimir, setPagamentoAImprimir] = useState<PagamentoDetalhe | null>(null);
  const dialogos = useDialogos();
  const { estado } = useAuth();
  const nomeEmpresa = estado?.conta?.nomeEmpresa ?? 'Oficina Ágil';

  const carregar = useCallback(() => {
    setCarregando(true);
    financeiroApi
      .obter(lancamentoId)
      .then(setDetalhe)
      .catch((e: unknown) => setErro(e instanceof Error ? e.message : 'Falha ao carregar.'))
      .finally(() => setCarregando(false));
  }, [lancamentoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const orcId = detalhe?.orcamentoId;
    if (!orcId) {
      setOrcamentoDetalhe(null);
      return;
    }
    let cancelado = false;
    orcamentosApi
      .obter(orcId)
      .then((d) => {
        if (!cancelado) setOrcamentoDetalhe(d);
      })
      .catch(() => {
        if (!cancelado) setOrcamentoDetalhe(null);
      });
    return () => {
      cancelado = true;
    };
  }, [detalhe?.orcamentoId]);

  async function preRenderRecibo(p: PagamentoDetalhe): Promise<void> {
    setPagamentoAImprimir(p);
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
  }

  async function imprimirRecibo(p: PagamentoDetalhe) {
    await preRenderRecibo(p);
    imprimirComClasse('imprimindo-recibo');
  }

  async function salvarPdfRecibo(p: PagamentoDetalhe) {
    await preRenderRecibo(p);
    const nome = `recibo-${detalhe?.descricao?.slice(0, 30) ?? 'baixa'}-${p.id.slice(0, 8)}.pdf`;
    const r = await salvarComoPdf('imprimindo-recibo', nome);
    if (!r.ok && !r.cancelado) {
      await dialogos.alertar(r.erro ?? 'Falha ao gerar PDF.');
    }
  }

  async function estornar(pagamentoId: string) {
    const motivo = await dialogos.solicitar({
      titulo: 'Estornar pagamento',
      mensagem: 'Motivo do estorno (opcional):',
      placeholder: 'Ex.: cliente desistiu, lançamento duplicado…',
      textoConfirmar: 'Continuar',
    });
    if (motivo === null) return;
    const ok = await dialogos.confirmar({
      titulo: 'Estornar pagamento',
      mensagem: 'Estornar este pagamento? O saldo será reaberto.',
      textoConfirmar: 'Estornar',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await financeiroApi.estornarPagamento(lancamentoId, pagamentoId, motivo || null);
      carregar();
      aoMudar();
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao estornar.');
    }
  }

  const totalBrutoItens = useMemo(
    () => arred(detalhe?.itens.reduce((acc, it) => acc + it.subtotalBruto, 0) ?? 0),
    [detalhe],
  );
  const totalDescontoItens = useMemo(
    () => arred(detalhe?.itens.reduce((acc, it) => acc + it.valorDesconto, 0) ?? 0),
    [detalhe],
  );
  const totalItens = useMemo(
    () => arred(detalhe?.itens.reduce((acc, it) => acc + it.subtotal, 0) ?? 0),
    [detalhe],
  );

  return (
    <Modal
      aberto
      tamanho="lg"
      titulo="Detalhes do lançamento"
      onFechar={aoFechar}
      rodape={
        <>
          <Button onClick={aoFechar}>Fechar</Button>
          {detalhe && detalhe.status === 'EmAberto' && detalhe.saldo > 0 && (
            <Button variante="primary" onClick={() => aoBaixar(detalhe)}>
              + Nova baixa
            </Button>
          )}
        </>
      }
    >
      {carregando ? (
        <p className="hint">Carregando…</p>
      ) : erro ? (
        <p className="alert alert--error" role="alert">
          {erro}
        </p>
      ) : detalhe ? (
        <div className="form-grid">
          {detalhe.orcamentoNumero && (
            <div className="info-banner">
              <div>
                <strong>Ordem de serviço {detalhe.orcamentoNumero}</strong>
                <div className="hint" style={{ marginTop: 2 }}>
                  {detalhe.clienteNome ? `Cliente ${detalhe.clienteNome}` : 'Cliente não informado'}
                </div>
              </div>
            </div>
          )}

          <CampoLeitura rotulo="Descrição">{detalhe.descricao}</CampoLeitura>

          <div className="grid-2">
            <CampoLeitura rotulo="Categoria">{detalhe.categoria || '—'}</CampoLeitura>
            <CampoLeitura rotulo="Valor">{fmtMoeda.format(detalhe.valor)}</CampoLeitura>
          </div>

          <div className="grid-2">
            <CampoLeitura rotulo="Emissão">
              {fmtData.format(new Date(detalhe.emissao))}
            </CampoLeitura>
            <CampoLeitura rotulo="Vencimento">
              {fmtData.format(new Date(detalhe.vencimento))}
            </CampoLeitura>
          </div>

          {detalhe.tipo === 'Receber' ? (
            <CampoLeitura rotulo="Cliente">{detalhe.clienteNome || '—'}</CampoLeitura>
          ) : (
            <div className="grid-2">
              <CampoLeitura rotulo="Fornecedor">{detalhe.fornecedor || '—'}</CampoLeitura>
              <CampoLeitura rotulo="Cliente associado">
                {detalhe.clienteNome || '—'}
              </CampoLeitura>
            </div>
          )}

          <div className="resumo-baixa">
            <div>
              <span className="resumo-baixa__rotulo">Tipo</span>
              <strong>{detalhe.tipo === 'Receber' ? 'A Receber' : 'A Pagar'}</strong>
            </div>
            <div>
              <span className="resumo-baixa__rotulo">Status</span>
              <strong>
                <BadgeStatus
                  status={detalhe.status}
                  vencido={detalhe.diasAtraso > 0}
                  parcial={detalhe.valorPago > 0 && detalhe.status === 'EmAberto'}
                />
              </strong>
            </div>
            <div>
              <span className="resumo-baixa__rotulo">Pago</span>
              <strong>{fmtMoeda.format(detalhe.valorPago)}</strong>
            </div>
            <div>
              <span className="resumo-baixa__rotulo">Saldo</span>
              <strong style={{ color: 'var(--color-primary)' }}>
                {fmtMoeda.format(detalhe.saldo)}
              </strong>
            </div>
          </div>

          {detalhe.observacoes && (
            <CampoLeitura rotulo="Observações">{detalhe.observacoes}</CampoLeitura>
          )}

          {detalhe.itens.length > 0 && (
            <fieldset className="fieldset">
              <legend className="fieldset__legend">Itens</legend>
              <ol className="itens-cards itens-cards--readonly">
                {detalhe.itens.map((it, idx) => (
                  <li key={it.id} className="itens-cards__item">
                    <div className="itens-cards__cabecalho">
                      <span className="itens-cards__indice">Item {idx + 1}</span>
                      <span className="badge badge--neutro">
                        {it.tipo === 'Produto' ? 'Peça' : it.tipo === 'Servico' ? 'Serviço' : 'Outro'}
                      </span>
                    </div>

                    <p className="hint itens-cards__descricao">{it.descricao}</p>

                    <div className="itens-cards__valores">
                      <CampoLeitura rotulo="Quantidade">{it.quantidade}</CampoLeitura>
                      <CampoLeitura rotulo="Valor unitário">
                        {fmtMoeda.format(it.valorUnitario)}
                      </CampoLeitura>
                      <CampoLeitura rotulo="Desconto">
                        {it.valorDesconto > 0
                          ? `− ${fmtMoeda.format(it.valorDesconto)}${
                              it.descontoTipo === 'Percentual' && it.descontoValor
                                ? ` (${it.descontoValor}%)`
                                : ''
                            }`
                          : '—'}
                      </CampoLeitura>
                    </div>

                    <div className="itens-cards__resumo">
                      <div className="itens-cards__resumo-linha">
                        <span>Subtotal bruto</span>
                        <span>{fmtMoeda.format(it.subtotalBruto)}</span>
                      </div>
                      {it.valorDesconto > 0 && (
                        <div className="itens-cards__resumo-linha itens-cards__resumo-linha--desc">
                          <span>Desconto</span>
                          <span>− {fmtMoeda.format(it.valorDesconto)}</span>
                        </div>
                      )}
                      <div className="itens-cards__resumo-linha itens-cards__resumo-linha--total">
                        <span>Subtotal</span>
                        <strong>{fmtMoeda.format(it.subtotal)}</strong>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="itens-cards__total">
                {totalDescontoItens > 0 ? (
                  <>
                    <div className="itens-cards__total-linha">
                      <span>Total bruto</span>
                      <span>{fmtMoeda.format(totalBrutoItens)}</span>
                    </div>
                    <div className="itens-cards__total-linha itens-cards__total-linha--desc">
                      <span>Total de desconto</span>
                      <span>− {fmtMoeda.format(totalDescontoItens)}</span>
                    </div>
                    <div className="itens-cards__total-linha itens-cards__total-linha--final">
                      <span>Total dos itens</span>
                      <strong>{fmtMoeda.format(totalItens)}</strong>
                    </div>
                  </>
                ) : (
                  <div className="itens-cards__total-linha itens-cards__total-linha--final">
                    <span>Total dos itens</span>
                    <strong>{fmtMoeda.format(totalItens)}</strong>
                  </div>
                )}
              </div>
            </fieldset>
          )}

          <fieldset className="fieldset">
            <legend className="fieldset__legend">
              Histórico de pagamentos ({detalhe.pagamentos.length})
            </legend>
            {detalhe.pagamentos.length === 0 ? (
              <p className="hint">Nenhuma baixa registrada ainda.</p>
            ) : (
              <ol className="timeline">
                {detalhe.pagamentos.map((p) => (
                  <li
                    key={p.id}
                    className={`timeline__item${p.estornado ? ' timeline__item--estornado' : ''}`}
                  >
                    <div className="timeline__cabecalho">
                      <strong>
                        {fmtMoeda.format(p.valor)}
                        {p.estornado && (
                          <span className="badge badge--cancelado" style={{ marginLeft: 8 }}>
                            Estornado
                          </span>
                        )}
                      </strong>
                      <span className="timeline__data">
                        {fmtDataHora.format(new Date(p.data))}
                      </span>
                    </div>
                    <div className="timeline__corpo">
                      {p.formaPagamento && (
                        <span>Forma: <strong>{ROTULO_FORMA[p.formaPagamento]}</strong></span>
                      )}
                      {p.observacao && <span>Obs.: {p.observacao}</span>}
                      {p.estornado && p.estornadoEm && (
                        <span style={{ color: 'var(--color-danger)' }}>
                          Estornado em {fmtDataHora.format(new Date(p.estornadoEm))}
                          {p.estornadoMotivo ? ` — ${p.estornadoMotivo}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="timeline__acoes">
                      <Button
                        onClick={() => void imprimirRecibo(p)}
                        aria-label="Imprimir recibo da baixa"
                      >
                        Imprimir recibo
                      </Button>
                      <Button
                        onClick={() => void salvarPdfRecibo(p)}
                        aria-label="Salvar recibo em PDF"
                      >
                        Salvar PDF
                      </Button>
                      {!p.estornado && (
                        <Button
                          className="btn--danger"
                          onClick={() => void estornar(p.id)}
                          aria-label="Estornar pagamento"
                        >
                          Estornar
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </fieldset>

          {pagamentoAImprimir && (
            <PrintPortal>
              <ReciboBaixaA4
                lancamento={detalhe}
                pagamento={pagamentoAImprimir}
                orcamento={orcamentoDetalhe}
                nomeEmpresa={nomeEmpresa}
                ordem={
                  detalhe.pagamentos.findIndex((p) => p.id === pagamentoAImprimir.id) + 1
                }
                totalBaixas={detalhe.pagamentos.length}
              />
            </PrintPortal>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
